import { useEffect, useMemo, useState } from 'react';
import { NavLink, ScrollArea, Stack, Text, TextInput } from '@mantine/core';
import { AppModal } from '../../shared/components/AppModal';
import { IconCheck, IconSearch } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useLocaleStore } from './localeStore';
import {
  LOCALE_OPTIONS,
  LOCALE_SEARCH_THRESHOLD,
  filterLocaleOptions,
} from './localeOptions';

interface Props {
  opened: boolean;
  onClose: () => void;
}

/**
 * The language chooser: a scrollable list of every supported language, with a
 * search box that appears once the list outgrows LOCALE_SEARCH_THRESHOLD.
 *
 * A list rather than a row of pills so it scales — pills only fit about four
 * before they wrap — and a modal rather than a submenu because submenus open on
 * hover, which doesn't work on the handhelds this app targets. One component
 * serves both the public pages and the signed-in account menu.
 */
export function LanguageModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const { locale, setLocale } = useLocaleStore();
  const [query, setQuery] = useState('');

  // Don't leave a stale search term behind for the next open.
  useEffect(() => {
    if (!opened) setQuery('');
  }, [opened]);

  const showSearch = LOCALE_OPTIONS.length > LOCALE_SEARCH_THRESHOLD;
  const options = useMemo(() => filterLocaleOptions(query), [query]);

  return (
    <AppModal
      opened={opened}
      onClose={onClose}
      title={t('languageSwitcher.label')}
      width="sm"
    >
      <Stack gap="sm">
        {showSearch && (
          <TextInput
            placeholder={t('languageSwitcher.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            leftSection={<IconSearch size={16} />}
            autoFocus
          />
        )}

        {options.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            {t('shell.noMatch')}
          </Text>
        ) : (
          <ScrollArea.Autosize mah={320} type="auto">
            <Stack gap={2}>
              {options.map((o) => {
                const selected = o.code === locale;
                return (
                  <NavLink
                    key={o.code}
                    label={o.native}
                    description={o.english !== o.native ? o.english : undefined}
                    active={selected}
                    color="invetrak"
                    variant="light"
                    rightSection={selected ? <IconCheck size={16} /> : null}
                    aria-current={selected ? 'true' : undefined}
                    onClick={() => {
                      setLocale(o.code);
                      onClose();
                    }}
                  />
                );
              })}
            </Stack>
          </ScrollArea.Autosize>
        )}
      </Stack>
    </AppModal>
  );
}
